import { useRouter } from "next/router";
import React, { useMemo } from "react";
import { defineMessages } from "react-intl";

import { formattedMessageParams } from "shared/messages";

import PriceFormatter from "@components/Formatters/PriceFormatter";
import SimpleSelect from "@components/UI/SimpleSelect";

import useSorting from "../../hooks/filters/useSorting";
import type { SortTypes } from "../../hooks/results/useSortFromQuery";

const convertToHourMins = (
  durationInMins: number | null | undefined
): string => {
  if (durationInMins === undefined || durationInMins === null) return "";
  const MINUTES_PER_HOUR = 60;
  const hourPart =
    durationInMins >= MINUTES_PER_HOUR
      ? `${Math.floor(durationInMins / MINUTES_PER_HOUR)} h`
      : "";
  return `${hourPart} ${durationInMins % MINUTES_PER_HOUR} min`;
};

const { sortOptionsFurtherOptions } = defineMessages({
  sortOptionsFurtherOptions: formattedMessageParams("Sorting.furtherOptions"),
});

const selectStyles = {
  control: (base: any) => ({
    ...base,
    backgroundColor: "transparent",
    boxShadow: "none",
    border: "none",
    justifyContent: "center",
  }),
  valueContainer: () => ({
    cursor: "pointer",
    display: "flex",
    flexWrap: "nowrap",
  }),
  singleValue: () => ({
    marginLeft: "2px",
    marginRight: "10px",
    maxWidth: "calc(100% - 8px)",
    whiteSpace: "nowrap",
  }),
  menu: (base: any) => ({
    ...base,
    animation: "fadeIn 0.2s ease",
    borderBottomLeftRadius: "5px",
    borderBottomRightRadius: "5px",
    boxShadow: "0 0 13px 0 rgba(158, 160, 172, 0.1)",
    marginTop: 0,
    borderRadius: 0,
    zIndex: 99999,
    maxWidth: "255px",
    right: 0,
  }),
};

interface SortProps {
  details: any;
  isHotels: boolean;
}

const Sort = React.memo<SortProps>(({ details, isHotels }) => {
  const router = useRouter();
  const {
    ref,
    events,
    isDesktop,
    changeSortFilter,
    sortOptions,
    sortOption,
    currentSortIndex,
    selectedStyle,
    onClickItem,
    customCurrency,
    intl,
  } = useSorting(isHotels);

  const mobileOptions = useMemo(() => {
    if (!isHotels && !details?.minimalDurationOpportunityCostPriceFlight) {
      return sortOptions.filter(
        (o) => o.value !== "MIN_DURATION_OPPORTUNITY_COST"
      );
    }
    return sortOptions;
  }, [
    isHotels,
    details?.minimalDurationOpportunityCostPriceFlight,
    sortOptions,
  ]);

  const useAlternativeCurrency = useMemo(() => !!router.query.currency, [
    router.query.currency,
  ]);

  if (!details || sortOptions.length === 0) {
    return null;
  }

  const renderMobileOptions = () => {
    return mobileOptions.map((option) => {
      const originalIndex = sortOptions.findIndex(
        (so) => so.value === option.value
      );
      return (
        <div
          key={option.value}
          role="button"
          className={`flights-sorting-item${selectedStyle(originalIndex)}`}
          onMouseUp={() => onClickItem(originalIndex)}
        >
          <div>{option.label}</div>
        </div>
      );
    });
  };

  const renderDesktopFlights = () => {
    const FIRST_SORT_ITEM_INDEX = 0;
    const SECOND_SORT_ITEM_INDEX = 1;
    const THIRD_SORT_ITEM_INDEX = 2;
    const FOURTH_SORT_ITEM_INDEX = 3;

    const getSimpleSelectValue = (thresholdIndex: number) => {
      return currentSortIndex < thresholdIndex
        ? {
            label: intl.formatMessage(sortOptionsFurtherOptions),
            value: "",
          }
        : sortOption;
    };

    return (
      <div ref={ref} className="container flights-sorting-wrapper">
        <div
          role="button"
          className={`flights-sorting-item${selectedStyle(
            FIRST_SORT_ITEM_INDEX
          )}`}
          onClick={() => onClickItem(FIRST_SORT_ITEM_INDEX)}
        >
          <div>{sortOptions[FIRST_SORT_ITEM_INDEX].label}</div>
          <div>
            <strong>
              <PriceFormatter
                price={
                  useAlternativeCurrency
                    ? details.minimalPriceFlight.alternativeCurrencyPrice
                    : details.minimalPriceFlight.price
                }
                customCurrency={customCurrency}
              />
            </strong>
          </div>
          <div>
            <span>
              ⌀ {convertToHourMins(details.minimalPriceFlight.length)}
            </span>
          </div>
        </div>
        {details.minimalDurationOpportunityCostPriceFlight && (
          <>
            <div className="vl" />
            <div
              role="button"
              className={`flights-sorting-item${selectedStyle(
                SECOND_SORT_ITEM_INDEX
              )}`}
              onClick={() => onClickItem(SECOND_SORT_ITEM_INDEX)}
            >
              <div>{sortOptions[SECOND_SORT_ITEM_INDEX].label}</div>
              <div>
                <strong>
                  <PriceFormatter
                    price={
                      useAlternativeCurrency
                        ? details.minimalDurationOpportunityCostPriceFlight
                            .alternativeCurrencyPrice
                        : details.minimalDurationOpportunityCostPriceFlight
                            .price
                    }
                    customCurrency={customCurrency}
                  />
                </strong>
              </div>
              <div>
                <span>
                  ⌀{" "}
                  {convertToHourMins(
                    details.minimalDurationOpportunityCostPriceFlight.length
                  )}
                </span>
              </div>
            </div>
          </>
        )}
        <div className="vl" />
        <div
          role="button"
          className={`flights-sorting-item${selectedStyle(
            THIRD_SORT_ITEM_INDEX
          )}`}
          onClick={() => onClickItem(THIRD_SORT_ITEM_INDEX)}
        >
          <div>{sortOptions[THIRD_SORT_ITEM_INDEX].label}</div>
          <div>
            <strong>
              <PriceFormatter
                price={
                  useAlternativeCurrency
                    ? details.minimalLengthFlight.alternativeCurrencyPrice
                    : details.minimalLengthFlight.price
                }
                customCurrency={customCurrency}
              />
            </strong>
          </div>
          <div>
            <span>
              ⌀ {convertToHourMins(details.minimalLengthFlight.length)}
            </span>
          </div>
        </div>
        <div className="vl" />
        <div className={`flights-sorting-item${selectedStyle()}`}>
          <SimpleSelect
            testId="filters-sorting"
            styles={selectStyles}
            isSearchable={false}
            value={getSimpleSelectValue(FOURTH_SORT_ITEM_INDEX)}
            options={sortOptions.slice(FOURTH_SORT_ITEM_INDEX)}
            clearIndicator={null}
            clearable
            onChange={(e) => changeSortFilter(e.value as SortTypes)}
          />
        </div>
      </div>
    );
  };

  const renderDesktopHotels = () => {
    const FIRST_SORT_ITEM_INDEX = 0;
    const SECOND_SORT_ITEM_INDEX = 1;
    const THIRD_SORT_ITEM_INDEX = 2;

    const getSimpleSelectValue = (thresholdIndex: number) => {
      return currentSortIndex < thresholdIndex
        ? {
            label: intl.formatMessage(sortOptionsFurtherOptions),
            value: "",
          }
        : sortOption;
    };

    return (
      <div className="container hotels-sorting-wrapper">
        <div
          role="button"
          className={`hotels-sorting-item${selectedStyle(
            FIRST_SORT_ITEM_INDEX
          )}`}
          onClick={() => onClickItem(FIRST_SORT_ITEM_INDEX)}
        >
          <div>{sortOptions[FIRST_SORT_ITEM_INDEX].label}</div>
          <div>
            <strong>
              <PriceFormatter
                price={details.minimalPriceHotel?.price}
                customCurrency={customCurrency}
              />{" "}
            </strong>
          </div>
        </div>
        <div className="vl" />
        <div
          role="button"
          className={`hotels-sorting-item${selectedStyle(
            SECOND_SORT_ITEM_INDEX
          )}`}
          onClick={() => onClickItem(SECOND_SORT_ITEM_INDEX)}
        >
          <div>{sortOptions[SECOND_SORT_ITEM_INDEX].label}</div>
          <div>
            <strong>
              <PriceFormatter
                price={details.maximalPriceHotel?.price}
                customCurrency={customCurrency}
              />
            </strong>
          </div>
        </div>
        <div className="vl" />
        <div className={`flights-sorting-item${selectedStyle()}`}>
          <SimpleSelect
            testId="filters-sorting"
            styles={selectStyles}
            isSearchable={false}
            value={getSimpleSelectValue(THIRD_SORT_ITEM_INDEX)}
            options={sortOptions.slice(THIRD_SORT_ITEM_INDEX)}
            clearIndicator={null}
            clearable
            onChange={(e) => changeSortFilter(e.value as SortTypes)}
          />
        </div>
      </div>
    );
  };

  if (isDesktop) {
    return isHotels ? renderDesktopHotels() : renderDesktopFlights();
  }

  return (
    <div
      ref={ref}
      {...events}
      role="button"
      className="container hotels-sorting-wrapper grabbing"
    >
      {renderMobileOptions()}
    </div>
  );
});

Sort.displayName = "Sort";

const SearchSorting = Sort;

export default SearchSorting;
